import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
} from '@eon/ui';
import { hooks, type OmInvitation } from '@eon/api-client';
import { formatDateTime } from '@eon/utils';
import { InvitationStatusBadge } from '@/components/status/StatusBadges';

interface PendingInvitationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PendingInvitationsDialog({ open, onOpenChange }: PendingInvitationsDialogProps) {
  const { t } = useTranslation();
  const list = hooks.useOmInvitations();
  const cancel = hooks.useOmCancelInvitation();
  const resend = hooks.useOmResendInvitation();

  const invitations = list.data?.data ?? [];

  const onCancel = (inv: OmInvitation) => {
    cancel.mutate(inv.id, {
      onSuccess: () => toast.success(t('group.invite.cancelled')),
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  const onResend = (inv: OmInvitation) => {
    resend.mutate(inv.id, {
      onSuccess: () => toast.success(t('group.invite.sent')),
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('portal.users.pendingTitle')}</DialogTitle>
          <DialogDescription>{t('portal.users.pendingDescription')}</DialogDescription>
        </DialogHeader>
        {list.isLoading ? (
          <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
        ) : invitations.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('portal.users.noInvitations')}</p>
        ) : (
          <ul className="divide-border max-h-80 divide-y overflow-y-auto">
            {invitations.map((inv) => (
              <li key={inv.id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{inv.email ?? inv.phone ?? inv.id}</p>
                  <p className="text-muted-foreground text-xs">
                    {inv.createdAt ? formatDateTime(inv.createdAt) : '—'}
                  </p>
                  <InvitationStatusBadge status={inv.status} />
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="outline" onClick={() => onResend(inv)} disabled={resend.isPending}>
                    {t('portal.users.resendInvitation')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => onCancel(inv)}
                    disabled={cancel.isPending}
                  >
                    {t('portal.users.cancelInvitation')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
